"use client";

import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { useOnboardingStore } from "../../lib/onboarding";
import { Globe, Smartphone } from "lucide-react";

const OnboardingStepPhone = () => {
  const [, setLocation] = useLocation();
  const { phoneNumber, setPhoneNumber, timezone, setTimezone } = useOnboardingStore();
  const [localTimezone, setLocalTimezone] = useState(timezone);
  const [timezones, setTimezones] = useState<string[]>([]);

  useEffect(() => {
    // Safely get timezones
    try {
      if (typeof Intl !== 'undefined' && (Intl as any).supportedValuesOf) {
        const zones = (Intl as any).supportedValuesOf('timeZone');
        setTimezones(zones);
      } else {
        // Fallback common timezones if supportedValuesOf is not available
        setTimezones([
          'UTC', 'Asia/Calcutta', 'America/New_York', 'Europe/London',
          'Asia/Singapore', 'Australia/Sydney', 'America/Los_Angeles'
        ]);
      }
    } catch (e) {
      console.error("Timezone detection failed", e);
      setTimezones(['UTC', 'Asia/Calcutta']);
    }

    // Auto-detect timezone if not set
    if (!timezone) {
      const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setLocalTimezone(detected);
      setTimezone(detected);
    }
  }, []);

  const handleNext = () => {
    setLocation("/onboarding/step-7");
  };

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="text-center">
        <h2 className="text-3xl font-bold text-slate-900 font-display">How should we nudge you?</h2>
        <p className="mt-2 text-slate-600">We'll send you timely reminders and weekly reflections to keep your journey on track.</p>
      </div>

      <div className="space-y-6">
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 font-sans">
            <Smartphone className="h-4 w-4 text-primary" />
            WhatsApp / Mobile Nudges (Optional)
          </label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder="+91 98765 43210"
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-sans"
          />
          <p className="text-xs text-slate-400 pl-1">
            Enter your number with country code for WhatsApp support.
          </p>
        </div>

        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 font-sans">
            <Globe className="h-4 w-4 text-primary" />
            Your Timezone
          </label>
          <select
            value={localTimezone}
            onChange={(e) => {
              setLocalTimezone(e.target.value);
              setTimezone(e.target.value);
            }}
            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all font-sans"
          >
            {timezones.map(zone => (
              <option key={zone} value={zone}>{zone}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={() => setLocation("/onboarding/step-5")}
          className="px-8 py-3 border border-slate-200 text-slate-600 font-semibold rounded-2xl hover:bg-slate-50 transition-all font-display"
        >
          Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          className="px-10 py-3 bg-primary !text-white font-bold rounded-2xl shadow-lg shadow-primary/20 hover:scale-105 active:scale-95 transition-all font-display"
        >
          Next Step
        </button>
      </div>
    </div>
  );
};

export default OnboardingStepPhone;
